import { isAttributedRater } from '@genoma/shared';

import { Brand } from '../../components/Brand.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { useSurveyStore } from '../../stores/survey.store.ts';

/**
 * What the rater sees first.
 *
 * Two jobs. It carries the reframe — three levels, place rather than grade,
 * "not observed" is a real answer — and it sets someone up to do this well:
 * greeted by name, told how long it takes, told they can stop and come back.
 * Somebody rushing through this on a phone between meetings gives worse
 * feedback than somebody who sat down for it, so the screen says so.
 */
export const IntroScreen = () => {
  const { t } = useI18n();
  const survey = useSurveyStore((state) => state.survey);
  const begin = useSurveyStore((state) => state.begin);

  if (!survey) return null;

  const isSelf = survey.kind === 'SELF';
  const isIndividual = survey.format === 'INDIVIDUAL';
  const subject = survey.evaluated[0];
  const firstName = subject?.fullName.split(' ')[0] ?? '';

  // A supervisor's assessment is shown on its own line, so promising them
  // anonymity would be a lie. They are told before they answer, not after.
  const isSupervisor = survey.evaluated.some((person) =>
    isAttributedRater(person.relationship),
  );

  const steps = [
    { title: t('survey.stepReadTitle'), body: t('survey.stepReadBody') },
    {
      title: t('survey.stepChooseTitle'),
      body: isSelf ? t('survey.selfStepChooseBody') : t('survey.stepChooseBody'),
    },
    {
      title: t('survey.stepObservedTitle'),
      body: isSelf ? t('survey.selfStepObservedBody') : t('survey.stepObservedBody'),
    },
    // Knowing the open question is coming changes how someone reads the twelve
    // behaviors — they notice the example they will want to cite.
    {
      title: t('survey.stepWriteTitle'),
      body: isSelf ? t('survey.selfStepWriteBody') : t('survey.stepWriteBody'),
    },
  ];

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col px-5">
      <Brand organization={survey.brand.organization} program={survey.brand.program} />

      <main className="flex-1 py-6">
        <span className="inline-flex rounded-[20px] border border-tint-line bg-tint px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.14em] text-teal-deep uppercase">
          {survey.cycle.name}
        </span>

        {survey.rater.firstName && (
          <p className="mt-4 text-lg font-semibold text-teal-deep">
            {t('survey.greeting', { name: survey.rater.firstName })}
          </p>
        )}

        <h1 className="mt-1.5 text-[28px] leading-[1.1] font-extrabold tracking-[-0.025em]">
          {isSelf
            ? t('survey.introTitleSelf')
            : isIndividual
              ? t('survey.introTitleIndividual')
              : t('survey.introTitleMultiple')}
        </h1>

        <p className="mt-3.5 max-w-[54ch] text-base text-soft">
          {isSelf
            ? t('survey.introLeadSelf')
            : isIndividual
              ? t('survey.introLeadIndividual')
              : t('survey.introLeadMultiple', { count: survey.evaluated.length })}
        </p>

        <p className="mt-3 max-w-[54ch] text-base text-soft">
          {isSelf ? t('survey.settleInSelf') : t('survey.settleIn')}
        </p>

        {!isSelf && isIndividual && subject && (
          <div className="mt-4 flex max-w-[460px] items-center gap-3 rounded-2xl border border-line bg-paper px-4 py-3.5 shadow-card">
            <span className="grid size-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#2AA0A0] to-teal-deep text-base font-bold text-white">
              {subject.initials}
            </span>
            <span>
              <span className="block text-[15px] font-bold">
                {t('survey.evaluating', { name: subject.fullName })}
              </span>
              <span className="block text-[12.5px] text-soft">
                {subject.jobTitle ?? t(`roleLevels.${subject.roleLevel}`)}
              </span>
            </span>
          </div>
        )}

        {/* Time and device sit together: both are about deciding when and where
            to do this, and both are easier to act on before starting. */}
        <div className="mt-5 max-w-[620px] rounded-2xl border border-tint-line bg-tint-soft px-4 py-3.5">
          <p className="text-[15px] font-bold text-teal-deep">
            {t('survey.timeEstimate', { minutes: survey.estimatedMinutes })}
          </p>
          <p className="mt-1 text-[13.5px] text-soft">{t('survey.timeEstimateHelp')}</p>
          <p className="mt-2 text-[13.5px] text-soft">
            {isSelf ? t('survey.deviceHintSelf') : t('survey.deviceHint')}
          </p>
        </div>

        <ul className="mt-5 max-w-[620px] list-none">
          {steps.map((step, index) => (
            <li
              key={step.title}
              className="flex gap-3 border-t border-line-soft py-2.5 text-[14.5px] first:border-t-0"
            >
              <span className="w-5 shrink-0 font-mono font-bold text-teal">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span>
                <b className="font-bold">{step.title}</b>{' '}
                <span className="text-soft">{step.body}</span>
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={begin}
          className="mt-5 cursor-pointer rounded-[13px] border-none bg-teal px-7 py-3.5 text-[15px] font-bold text-white shadow-action transition hover:bg-teal-deep"
        >
          {t('common.start')}
        </button>

        <p className="mt-4 max-w-[620px] text-xs text-faint">
          {isSelf
            ? t('survey.selfPurpose')
            : isSupervisor
              ? t('survey.anonymityNoticeSupervisor')
              : t('survey.anonymityNotice')}
          {!isSelf && isIndividual && firstName
            ? ` ${t('survey.anonymityPurpose', { name: firstName })}`
            : ''}
        </p>

        {/* The full notice, last and quiet. The line above is what a rater
            needs before answering; this is for whoever wants the workings, and
            it opens in its own tab so nobody loses the questionnaire to it. */}
        <a
          href="/privacidad"
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-block text-xs text-faint underline underline-offset-2 transition hover:text-teal"
        >
          {t('privacy.link')}
        </a>
      </main>
    </div>
  );
};
